// Lib
import { lowerFirst } from 'lodash/fp';

// Utils
import simpleTimeFormatter from '../../../../../common/utils/datetime/simpleTimeFormatter';
import {
    getDueDate,
    getHasDueDateTime,
    getIsTaskComplete,
    getShowDueDate,
} from '../../../../../common/elements/utils/elementPropertyUtils';
import { getIsDueToday, getIsOverdue } from '../../../../../common/utils/timeUtil';
import { friendlyDateFormatterIntl } from '../../../../../common/utils/datetime/friendlyDateFormatterIntl';
import { FRIENDLY_DATE_FORMAT_STYLES } from '../../../../../common/utils/datetime/intlDateFormatPresets';
import { getFriendlyTranslatedTime } from '../../../../utils/i18n/translationHelpers';
import { DEFAULT_TIME_FORMAT } from '../../../../../common/users/timeFormatConstants';

export const getDueDatePopupId = (elementId) => `task-due-date-${elementId}`;

export const getElementIsOverdue = (element) => {
    if (getIsTaskComplete(element)) return false;

    const dueDate = new Date(getDueDate(element));
    const hasDueDateTime = getHasDueDateTime(element);

    return getIsOverdue(dueDate, hasDueDateTime);
};

export const getElementIsDueToday = (element) => {
    if (getIsTaskComplete(element)) return false;

    const dueDate = new Date(getDueDate(element));
    return getIsDueToday(dueDate);
};

/**
 * Get the due date pill content with translation support
 * @param {object} element - The task element
 * @param {function} translate - The translation function from useTranslation hook (react-i18next t function)
 * @param {string} userLocale - The user's locale for date formatting
 * @param {string} timeFormat - The user's time format preference ('12h' or '24h')
 * @returns {string} The formatted due date text
 */
export const getDueDatePillContent = (element, translate, userLocale, timeFormat = DEFAULT_TIME_FORMAT) => {
    const dueDateValue = getDueDate(element);

    if (!getShowDueDate(element) || !dueDateValue) {
        return translate('assignment.dueDatePlaceholder');
    }

    const dueDate = new Date(dueDateValue);

    // Show the relative time if it's overdue and in the past
    if (getElementIsDueToday(element) && getElementIsOverdue(element)) {
        const duePrefix = translate('assignment.dueDatePrefix');
        const friendlyTimeText = getFriendlyTranslatedTime(dueDate.getTime(), true, userLocale);
        return `${duePrefix} ${lowerFirst(friendlyTimeText)}`;
    }

    const dateText = lowerFirst(
        friendlyDateFormatterIntl({
            timestamp: dueDate,
            locale: userLocale,
            style: FRIENDLY_DATE_FORMAT_STYLES.COMPACT,
        }),
    );

    const duePrefix = translate('assignment.dueDatePrefix');
    const dueText = `${duePrefix} ${dateText}`;

    return getHasDueDateTime(element) ? `${dueText} (${simpleTimeFormatter(dueDate, timeFormat)})` : dueText;
};

export const getDueDateClass = (element) => {
    if (!getShowDueDate(element) || !getDueDate(element)) return '';

    if (getIsTaskComplete(element)) return 'completed';

    if (getElementIsOverdue(element)) return 'overdue';

    if (getElementIsDueToday(element)) return 'due-today';

    return 'due-future';
};
